import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission, PlatformWidePermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { manyItems, oneItem } from '../shared'
import { isoDatetimeToDate } from '../zod-extra'
import { roleTypeSchema } from './roletype'
import { accountQueryKey } from './shared'

/**
 * Defines the expected values of plan types and maps to the records in the
 * plan_type table
 */
export enum PlanType {
  None = 'NONE',
  Standard = 'STANDARD',
  Trial = 'TRIAL',
  Dealer = 'DEALER'
}

/**
 * Zod schema for plan types
 */
export const planTypeSchema = z.enum(PlanType)

const planTypeValues = Object.values(PlanType)

/**
 * Checks if the string is a valid plan type
 *
 * @param s
 */
export const isPlanType = (s: string | null): s is PlanType =>
  planTypeValues.includes(s as PlanType)

const organizationDetailSchema = z.object({
  orgCode: z.string(),
  name: z.string(),
  ownerEmail: z.string(),
  /**
   * Does the operation consent to Precision Planting support
   * members accessing their data?
   */
  supported: z.boolean().optional().default(false),
  /**
   * The type of plan the operation is on
   */
  planType: planTypeSchema,
  /**
   * The date the plan type expires
   */
  planTypeExpiresAt: z.coerce.date().optional(),
  /**
   * Whether or not the operation has opted out of sharing data with
   * third-party LLM providers for Aurora features.
   */
  llmDataSharingDisabled: z.boolean().default(false),
  /**
   * When the operation was first created
   */
  createdAt: z.coerce.date(),
  /**
   * When the operation was last updated (e.g., plan type changed, name changed)
   */
  updatedAt: z.coerce.date(),
  /**
   * The name that the operation owner entered when they created their account
   */
  ownerDisplayName: z.string()
})

const userOrganizationsSchema = organizationDetailSchema
  .extend({
    /**
     * The role the user has in the operation.
     */
    userRoleType: roleTypeSchema
  })
  .transform(({ name, ...item }) => ({ ...item, orgName: name }))

/**
 * Api for getting the organizations that a user is a member of
 */
export const userOrganizationsApi = defineApi({
  pathSchema: z.object({
    username: z.string()
  }),
  pathFor: (p) => `/useraccounts/${p.username}/organizations`,
  name: 'GetUserOrganizations',
  description: 'Retrieve all organizations that a user is a member of with their role',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: manyItems(userOrganizationsSchema),
  gatewayPath: '/useraccounts/:username/organizations' as const
})

/**
 * Api for getting the details of an individual operation
 */
export const organizationDetailsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}`,
  name: 'GetOrganizationDetails',
  description: 'Retrieve detailed information about a specific organization',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: manyItems(organizationDetailSchema),
  gatewayPath: '/organizations/:orgCode' as const,
  queryKey: (p) => [p.orgCode, 'org-details']
})

/**
 * Api for getting the details of all operations
 *
 * Optionally include operations that are disabled
 * Optionally filter out operations that are not supported
 */
export const adminOrganizationsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    includeDisabled: z.boolean().optional(),
    onlySupported: z.boolean().optional()
  }),
  pathFor: ({ orgCode, ...filters }) => path(`/organizations/${orgCode}`, filters),
  name: 'AdminGetOrganizations',
  description: 'Retrieve organizations with admin filtering options',
  requiredPermissions: PlatformWidePermission.AdministerPlatform,
  responseSchema: manyItems(organizationDetailSchema),
  gatewayPath: '/organizations/:orgCode' as const
})

export const internalAdminOrganizationsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: z.object({
    includeDisabled: z.boolean().optional(),
    onlySupported: z.boolean().optional()
  }),
  pathFor: ({ orgCode, ...filters }) => `/internal${path(`/organizations/${orgCode}`, filters)}`,
  name: 'InternalAdminGetOrganizations',
  description: 'Internal endpoint for retrieving organizations with admin filtering',
  requiredPermissions: PlatformWidePermission.AdministerPlatform,
  responseSchema: manyItems(organizationDetailSchema),
  gatewayPath: '/internal/organizations/:orgCode' as const
})

export const internalUpdateOrganizationPlanApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/internal/organizations/${p.orgCode}/plan`,
  name: 'InternalUpdateOrganizationPlan',
  description: 'Update organization subscription plan type and expiration date',
  requiredPermissions: PlatformWidePermission.AdministerPlatform,
  method: 'PUT',
  requestBodySchema: z.object({
    planType: planTypeSchema,
    expiresAt: isoDatetimeToDate,
    billedOrgCode: z.string()
  }),
  responseSchema: z.void(),
  gatewayPath: '/internal/organizations/:orgCode/plan' as const,
  supportsCodec: true
})

/**
 * Api for transferring ownership of an operation to a manager
 * (the current owner becomes a manager)
 */
export const transferOwnershipApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    username: z.string()
  }),
  pathFor: ({ orgCode, username }) => `/organizations/${orgCode}/promote/${username}`,
  name: 'TransferOrganizationOwnership',
  description: 'Transfer organization ownership to a manager (current owner becomes manager)',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/promote/:username' as const
})

export const updateLLMDataSharingStatusApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    enabled: z.boolean()
  }),
  pathFor: (p) =>
    `/organizations/${p.orgCode}/features/llm-data-sharing/${p.enabled ? 'enable' : 'disable'}`,
  name: 'UpdateLLMDataSharingStatus',
  description: 'Enable or disable sharing organization data with third-party LLM providers',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/features/llm-data-sharing/:enabled' as const,
  invalidateQueries: (p) => [organizationDetailsApi.queryKey(p), [accountQueryKey]]
})

export const updateSupportSharingStatusApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    enabled: z.boolean()
  }),
  pathFor: (p) =>
    `/organizations/${p.orgCode}/features/supported/${p.enabled ? 'enable' : 'disable'}`,
  name: 'UpdateSupportSharingStatus',
  description: 'Enable or disable Precision Planting support access to organization data',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/features/supported/:enabled' as const,
  invalidateQueries: (p) => [organizationDetailsApi.queryKey(p), [accountQueryKey]]
})

const dealerRelationshipSchema = z.object({
  orgCode: z.string(),
  name: z.string(),
  ownerDisplayName: z.string(),
  ownerEmail: z.string(),
  dealerPaysSubscription: z.boolean().default(false),
  planType: planTypeSchema,
  planTypeExpiresAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
})

const farmerOrgsQueryKey = 'farmerOrgs'

export const listFarmerOrgsForDealerApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/farmer-orgs`,
  name: 'ListFarmerOrgsForDealer',
  description: 'List farmer organizations managed by a dealer',
  requiredPermissions: OperationLevelPermission.ManageDataSharing,
  responseSchema: manyItems(dealerRelationshipSchema),
  gatewayPath: '/organizations/:orgCode/farmer-orgs' as const,
  queryKey: (p) => [farmerOrgsQueryKey, p.orgCode]
})

export const createFarmerOrgForDealerApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/farmer-orgs`,
  name: 'CreateFarmerOrgForDealer',
  description: 'Create a new farmer organization under a dealer',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'POST',
  requestBodySchema: z.object({
    name: z.string()
  }),
  responseSchema: oneItem(
    z.object({
      orgCode: z.string()
    })
  ),
  gatewayPath: '/organizations/:orgCode/farmer-orgs' as const,
  invalidateQueries: (p) => [farmerOrgsQueryKey, p?.orgCode]
})

export const createHandoffInvitationApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    farmerOrgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/farmer-orgs/${p.farmerOrgCode}/handoff`,
  name: 'CreateHandoffInvitation',
  description: 'Create a handoff invitation to transfer farmer org ownership to a new user',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'POST',
  requestBodySchema: z.object({
    email: z.string()
  }),
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/farmer-orgs/:farmerOrgCode/handoff' as const
})

export const disconnectFarmerOrgApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    farmerOrgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/farmer-orgs/${p.farmerOrgCode}`,
  name: 'DisconnectFarmerOrg',
  description: 'Disconnect a farmer organization from a dealer',
  requiredPermissions: OperationLevelPermission.ManageOperationDetails,
  method: 'DELETE',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/farmer-orgs/:farmerOrgCode' as const,
  invalidateQueries: (p) => [farmerOrgsQueryKey, p?.orgCode]
})

export const connectDealerToOrgApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    dealerOrgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/dealers/${p.dealerOrgCode}`,
  name: 'ConnectDealerToOrg',
  description: 'Connect a dealer to a farmer organization for data sharing',
  requiredPermissions: OperationLevelPermission.ManageDataSharing,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/dealers/:dealerOrgCode' as const
})

export const disconnectDealerFromOrgApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    dealerOrgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/dealers/${p.dealerOrgCode}`,
  name: 'DisconnectDealerFromOrg',
  description: 'Disconnect a dealer from a farmer organization',
  requiredPermissions: OperationLevelPermission.ManageDataSharing,
  method: 'DELETE',
  responseSchema: z.void(),
  gatewayPath: '/organizations/:orgCode/dealers/:dealerOrgCode' as const,
  invalidateQueries: (p) => ['dealers', p?.orgCode]
})

export const listDealersForOrganizationApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/dealers`,
  name: 'ListDealersForOrganization',
  description: 'List the dealers advising a specific organization',
  requiredPermissions: OperationLevelPermission.ManageDataSharing,
  responseSchema: manyItems(dealerRelationshipSchema),
  gatewayPath: '/organizations/:orgCode/dealers' as const,
  queryKey: (p) => ['dealers', p.orgCode]
})

export const internalListDealersForOrganizationApi = defineApi({
  ...listDealersForOrganizationApi,
  pathFor: (p) => `/internal/organizations/${p.orgCode}/dealers`,
  name: 'InternalListDealersForOrganization',
  description: 'Internal endpoint for listing the dealers advising a specific organization',
  requiredPermissions: PlatformWidePermission.AdministerPlatform,
  gatewayPath: '/internal/organizations/:orgCode/dealers' as const
})

const invitationResponseSchema = z.object({
  orgCode: z.string()
})

export const acceptDealerInviteApi = defineApi({
  pathSchema: z.object({
    inviteCode: z.string()
  }),
  querySchema: z.object({
    dealerName: z.string()
  }),
  pathFor: (p) => path(`/invitations/${p.inviteCode}/accept`, { dealerName: p.dealerName }),
  name: 'AcceptDealerInvite',
  description: 'Accept a dealer invite and create a dealer organization',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'PUT',
  responseSchema: oneItem(invitationResponseSchema),
  gatewayPath: '/invitations/:inviteCode/accept' as const,
  invalidateQueries: () => [accountQueryKey]
})

export const acceptHandoffInviteApi = defineApi({
  pathSchema: z.object({
    inviteCode: z.string()
  }),
  pathFor: (p) => `/invitations/${p.inviteCode}/accept`,
  name: 'AcceptHandoffInvite',
  description: 'Accept a handoff invite and take ownership of an operation',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  method: 'PUT',
  responseSchema: oneItem(invitationResponseSchema),
  gatewayPath: '/invitations/:inviteCode/accept' as const,
  invalidateQueries: () => [accountQueryKey]
})

export const organizationMetadataApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/organizations/${p.orgCode}/metadata`,
  name: 'GetOrganizationMetadata',
  description: 'Retrieve metadata (name) for a specific organization',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: oneItem(z.object({ name: z.string() })),
  gatewayPath: '/organizations/:orgCode/metadata' as const
})

export const createDealerInviteApi = defineApi({
  pathFor: () => '/invitations/dealer-orgs',
  name: 'CreateDealerInvite',
  description: 'Send a dealer invite email to create a new dealer organization',
  requiredPermissions: PlatformWidePermission.SupportUsers,
  method: 'POST',
  requestBodySchema: z.object({
    email: z.string(),
    dealerName: z.string()
  }),
  responseSchema: z.void(),
  gatewayPath: '/invitations/dealer-orgs' as const
})

export const rollbackOrgCreationApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => `/internal/organizations/${p.orgCode}/rollback`,
  name: 'RollbackOrganizationCreation',
  description:
    'Rollback the creation of an organization in the case of a failure during onboarding',
  requiredPermissions: PlatformWidePermission.AdministerPlatform,
  method: 'PUT',
  responseSchema: z.void(),
  gatewayPath: '/internal/organizations/:orgCode/rollback' as const
})

export namespace Organization {
  export type Detail = z.infer<typeof organizationDetailSchema>
  export type UserOrganization = z.infer<typeof userOrganizationsSchema>
  export type User = z.infer<typeof userOrganizationsSchema>
}
